/**
 * ADR-0263 — ops2026 "Pricing page" preview panel.
 *
 * Lives as a sibling tab inside the Plans view. Fetches
 * `/internal/admin/pricing-preview` (same wire shape as the public
 * `/v1/public/pricing` plus draft rows) and renders the shared
 * `<PricingTable>` from `@sill/ui`. The founder uses this to see
 * exactly what sill.so/pricing will look like BEFORE flipping a draft
 * plan to active (activating a default plan has side effects).
 */

import { useEffect, useState } from 'react';

import { PricingTable, type PricingTableData } from '@sill/ui';
import '@sill/ui/css/pricing';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

type FetchState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly data: PricingTableData }
  | { readonly kind: 'error'; readonly message: string };

export function PricingPagePreviewPanel(): JSX.Element {
  const api = useAdminApi();
  const C = useC();
  const [state, setState] = useState<FetchState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async (): Promise<void> => {
      try {
        const res = await api.getPricingPreview();
        if (cancelled) return;
        setState({ kind: 'ready', data: res });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setState({ kind: 'error', message: `Could not load pricing preview${reason}.` });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (state.kind === 'loading') {
    return (
      <Card style={{ padding: 24 }}>
        <div
          className="text-center"
          style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1.5 }}
        >
          Loading…
        </div>
      </Card>
    );
  }
  if (state.kind === 'error') {
    return (
      <Card style={{ padding: 16 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.coralFg,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Load failed
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>{state.message}</div>
      </Card>
    );
  }
  return (
    <div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.body,
          marginBottom: 12,
        }}
      >
        Preview of sill.so/pricing. Active plans/add-ons show live; draft rows carry a
        "Draft" badge. Activating a default plan is a staff write; this view is read-only.
      </div>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <PricingTable data={state.data} theme={C} showDraftBadges agenciesHref="/agencies" />
      </Card>
    </div>
  );
}
